import { ArrowsUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { flatten } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import InlineErrorMessage from '@/components/InlineErrorMessage/InlineErrorMessage'
import Loading from '@/components/Loading'
import Modal from '@/components/Modal'
import RoundSearchBar from '@/components/TailwindUI/RoundSearchBar'
import SuggestionBox, { SuggestionTypes } from '@/pages/SpaceAppBuilder/components/SuggestionBox/SuggestionBox'
import {
  AppInstanceDataSource,
  DatasourceWizardMode,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3CatalogQueryOptions, dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'

import WorkgridDatasourceConfigurationForm from './components/WorkgridDataSourceConfigurationForm'
import messages from './messages'

//<editor-fold desc="Styled Components">
const StyledModal = styled(Modal)`
  max-height: 700px;
  @media only screen and (min-width: 992px) {
    width: max(70%, 850px) !important;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem;
  min-height: 400px;
`

const StyledFlexContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  justify-content: space-between;
`

const DataSourcesContentContainer = styled.div`
  display: flex;
  max-height: 500px;
  flex-direction: column;
  width: 100%;
  overflow-y: scroll;
`
const DataSourceCountContainer = styled.div`
  margin-bottom: 1rem;
  margin-left: 1rem;
`
const DataSourceStatusContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`
const DataSourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`
const DataSourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;
  text-align: center;
  justify-content: space-evenly;
  width: 150px;
  min-height: 140px;
  margin: 0.5em;
  padding: 1em 3em 1em 3em;
  border: 1px solid rgb(229 231 235 / 1);
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border: 1px solid rgb(37 99 235 / 1);
  }

  &.DataSourceUnavailableContainer {
    filter: opacity(0.5);
    &:hover {
      border: 2px solid #f2f4f6;
      cursor: default;
    }
  }
`

const DataSourceIcon = styled.img`
  height: 48px;
  width: 48px;
  display: inline-block;
  border-radius: 5px;
`

const DataSourceName = styled.span`
  font-size: 0.8em;
  font-weight: bold;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 700px;
  overflow-x: hidden;
  overflow-y: hidden;
  margin-right: auto;
  margin-left: auto;
`

const StyledUnavailableDiv = styled.div`
  display: flex;
  width: 100%;
  background-color: #f0f0f0;
  margin-top: 2rem;
  padding: 10px 25px;
  border-radius: 8px;
  align-items: baseline;
`

const StyledUnavailableMessage = styled.h3`
  padding: 10px;
`

const StyledUnavailableButton = styled.button`
  display: flex;
  background-color: white;
  border: 1px solid var(--wg-dark-blue);
  border-radius: 5px;
  padding: 12px;
  color: var(--wg-dark-blue);
  align-content: center;
  height: 70%;
  margin-left: 5px;

  :hover {
    background-color: white;
    border: 1px solid var(--wg-dark-blue);
    color: var(--wg-dark-blue);
  }
`
//</editor-fold>

function stringIncludesCaseInsensitive(value = '', filter = '') {
  return value.toLowerCase().includes(filter.toLowerCase())
}

//<editor-fold desc="Interfaces">
interface CreateDataSourceModalProps {
  isOpen?: boolean
  showInstanceSelection?: boolean
  datasourceKey?: string
  datasourceType?: string
  mode: DatasourceWizardMode
  onClose: () => void
  appDataSources?: Array<AppInstanceDataSource>
  testDatasourceInstance?: boolean
  onSave?: (dataSource: any) => void
  value?: WorkgridDatasource
}

export interface PollingIntervalStep {
  label: string
  value: string
}
//</editor-fold>

const CreateDataSourceModal = ({
  showInstanceSelection = false,
  isOpen = true,
  mode,
  datasourceKey = '',
  appDataSources,
  onClose,
  testDatasourceInstance,
  onSave,
  value
}: CreateDataSourceModalProps) => {
  const intl = useIntl()
  const [filter, setFilter] = useState('')
  const [selectedDataSourceKey, setSelectedDataSourceKey] = useState('')
  const [isShowingSuggestionBox, setIsShowingSuggestionBox] = useState(false)
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()

  const workgridDatasourcesCatalogQueryResult = useQuery<WorkgridCatalogDatasource[]>({
    ...dataSourceV3CatalogQueryOptions({ spaceId }),
    enabled: mode === DatasourceWizardMode.CREATE
  })

  const dataSourcesV3Query = useQuery<WorkgridDatasource[]>({
    ...dataSourceV3QueryOptions({ spaceId })
  })

  const matchedWorkgridDataSourceInstances =
    dataSourcesV3Query.data?.filter(ds => ds.datasourceId === selectedDataSourceKey) ?? []

  let dataSourcesArr
  if (mode === DatasourceWizardMode.CREATE) {
    const filteredWorkgridDatasources = workgridDatasourcesCatalogQueryResult.data
    dataSourcesArr = filteredWorkgridDatasources
  } else {
    dataSourcesArr = workgridDatasourcesCatalogQueryResult.data
  }

  const filteredDataSources: WorkgridCatalogDatasource[] =
    mode === DatasourceWizardMode.CREATE
      ? dataSourcesArr
          ?.filter(ds => (appDataSources ? appDataSources?.every(ads => ads.key !== ds.id) : true))
          .filter(
            dataSource =>
              stringIncludesCaseInsensitive(dataSource.name, filter) ||
              stringIncludesCaseInsensitive(dataSource.id, filter)
          ) || []
      : dataSourcesArr || []

  const selectedDataSource = filteredDataSources?.find(dataSource => dataSource.id === selectedDataSourceKey)

  useEffect(() => {
    setSelectedDataSourceKey(datasourceKey)
  }, [datasourceKey])

  const addFallbackImageSrc = async (event: any) => {
    // TODO: Replace with a proper fallback icon
    event.target.src = 'https://static.thenounproject.com/png/1907400-200.png'
  }

  const closeModal = () => {
    if (mode === DatasourceWizardMode.CREATE) {
      setSelectedDataSourceKey('')
    }
    setIsShowingSuggestionBox(false)
    dataSourcesV3Query.refetch()
    setFilter('')
    onClose()
  }

  const renderDataSourcesByStatus = (
    title: MessageDescriptor,
    dataSources: WorkgridCatalogDatasource[][],
    isDataSourceAvailable: boolean = true
  ) => {
    return (
      flatten(dataSources).length > 0 && (
        <>
          <DataSourceCountContainer className={'DataSourceCountContainer'}>
            <FormattedMessage {...title} />
            {' (' + flatten(dataSources).length + ')'}
          </DataSourceCountContainer>
          <DataSourcesContainer>
            {dataSources.map((dataSourcesByStatus, i) => (
              <DataSourceStatusContainer key={`data-source-by-status-${i}`}>
                {dataSourcesByStatus.map(dataSource => (
                  <DataSourceContainer
                    className={isDataSourceAvailable ? '' : 'DataSourceUnavailableContainer'}
                    key={dataSource.id}
                    onClick={() => {
                      setSelectedDataSourceKey(isDataSourceAvailable ? dataSource.id : '')
                    }}
                  >
                    <DataSourceIcon
                      id={`app-icon-${dataSource.id}`}
                      src={dataSource.iconUrl}
                      onError={addFallbackImageSrc}
                    />
                    <DataSourceName>{dataSource.name}</DataSourceName>
                  </DataSourceContainer>
                ))}
              </DataSourceStatusContainer>
            ))}
          </DataSourcesContainer>
        </>
      )
    )
  }

  const openSuggestionBox = () => {
    setIsShowingSuggestionBox(true)
  }

  const handleBack = () => {
    if (mode === DatasourceWizardMode.CREATE) {
      setSelectedDataSourceKey('')
    } else {
      closeModal()
    }
  }

  return (
    <StyledModal isOpen={isOpen} onClose={closeModal} className="w-full max-w-3xl p-1 sm:px-4 sm:py-6 lg:min-w-[900px]">
      <Content>
        {!workgridDatasourcesCatalogQueryResult.isLoading && (
          <div className="flex items-center justify-between">
            <div className="flex">
              {selectedDataSource ? (
                <>
                  <img
                    id={`app-icon-${selectedDataSource.id}`}
                    src={selectedDataSource.iconUrl}
                    alt="data source configuration icon"
                    className="h-14 w-14 rounded-lg"
                    onError={addFallbackImageSrc}
                  />
                  <div className="ml-4">
                    <h1 className="text-2xl font-semibold text-zinc-900">
                      {selectedDataSource?.name || selectedDataSource?.id}
                    </h1>
                    <p className="text-base font-normal text-zinc-500">Configuration details</p>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-x-4">
                  <ArrowsUpDownIcon className="h-4 w-4 rounded-full stroke-zinc-800 stroke-2 text-zinc-800 ring-2 ring-zinc-800 ring-offset-1" />
                  <h1 className="font-bold text-zinc-900">Add a data source</h1>
                </div>
              )}
            </div>
            <div className="flex items-center">
              <button onClick={closeModal}>
                <XMarkIcon className="h-8 w-8 text-zinc-500 hover:text-zinc-900" />
              </button>
            </div>
          </div>
        )}

        {!workgridDatasourcesCatalogQueryResult.isLoading &&
          mode === DatasourceWizardMode.CREATE &&
          !selectedDataSourceKey && (
            <div className="mb-8 ml-2 mt-2 flex w-96">
              <RoundSearchBar
                placeholder="Search data sources"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                onClear={() => setFilter('')}
              />
            </div>
          )}

        <StyledFlexContainer>
          {workgridDatasourcesCatalogQueryResult.isLoading &&
            mode === DatasourceWizardMode.CREATE &&
            !datasourceKey && (
              <LoadingContainer>
                <Loading
                  isLoading={workgridDatasourcesCatalogQueryResult.isLoading}
                  loadingMessage={intl.formatMessage(messages.loading)}
                />
              </LoadingContainer>
            )}

          {workgridDatasourcesCatalogQueryResult.error ? (
            <InlineErrorMessage message={intl.formatMessage(messages.datasourceCatalogError)} />
          ) : (
            mode === DatasourceWizardMode.CREATE &&
            !selectedDataSourceKey &&
            !workgridDatasourcesCatalogQueryResult.isLoading && (
              <DataSourcesContentContainer className="DataSourcesContentContainer">
                {renderDataSourcesByStatus(messages.availableDataSources, [filteredDataSources])}
                <StyledUnavailableDiv>
                  {!isShowingSuggestionBox && (
                    <>
                      <StyledUnavailableMessage>
                        <FormattedMessage {...messages.dataSourceUnavailable} />
                      </StyledUnavailableMessage>
                      <StyledUnavailableButton onClick={openSuggestionBox}>
                        <FormattedMessage {...messages.dataSourceSuggest} />
                      </StyledUnavailableButton>
                    </>
                  )}
                  <SuggestionBox
                    isShowingModal={isShowingSuggestionBox}
                    setIsShowingModal={setIsShowingSuggestionBox}
                    suggestionType={SuggestionTypes.dataSource}
                    labelText={messages.dataSourceUnavailable}
                  />
                </StyledUnavailableDiv>
              </DataSourcesContentContainer>
            )
          )}
          {selectedDataSourceKey && (
            <div className="mt-4 w-full">
              <WorkgridDatasourceConfigurationForm
                datasourceKey={selectedDataSourceKey}
                mode={
                  mode === DatasourceWizardMode.PREVIEW
                    ? DatasourceWizardMode.PREVIEW
                    : matchedWorkgridDataSourceInstances.length > 0 && showInstanceSelection
                      ? DatasourceWizardMode.EDIT
                      : appDataSources?.find(datasource => datasource.key === selectedDataSourceKey)
                        ? DatasourceWizardMode.EDIT
                        : mode
                }
                showInstanceSelection={matchedWorkgridDataSourceInstances.length > 0 && showInstanceSelection}
                currentUserOwnedWorkgridDataSource={
                  value || matchedWorkgridDataSourceInstances?.find(ds => appDataSources?.find(ads => ads.id === ds.id))
                }
                currentApplicationDatasource={appDataSources?.find(ads => ads.key === selectedDataSourceKey)}
                onClose={closeModal}
                onBack={datasourceKey ? undefined : handleBack}
                appId={appId}
                testDatasourceInstance={testDatasourceInstance}
                onSave={onSave}
              />
            </div>
          )}
        </StyledFlexContainer>
      </Content>
    </StyledModal>
  )
}
export default CreateDataSourceModal
